import {
	ContractSuretyAccountUnderwritingModelSize,
	contractSuretyAccountUnderwritingModelSizeSchema,
	type SuretyAccountId,
	SuretyType,
} from "@inrev/common";
import {
	useAdminFetchLatestSuretyAccountUnderwriting,
	useAdminUpdateModelSize,
} from "@portal/api/account/admin";
import { FormField, FormItemLabel } from "@portal/components/form";
import { FormDropdown } from "@portal/components/form/fields";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { LineItem } from "@portal/components/ui/LineItem";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { typedZodResolver } from "../../../../../utils/form";
import {
	AdminEditModalLayout,
	AdminSectionHeaderLayout,
	AdminSectionLayout,
} from "../../../../admin/account/shared/AdminSectionLayout";

type AdminModelSizeFormData = {
	modelSize: ContractSuretyAccountUnderwritingModelSize;
};

const modelSizeToFormData = (
	modelSize: ContractSuretyAccountUnderwritingModelSize,
): AdminModelSizeFormData => ({
	modelSize,
});

export const AdminAccountModelSizeSection = ({ accountId }: { accountId: SuretyAccountId }) => {
	const [isEditing, setIsEditing] = useState(false);
	const { latestUnderwriting, latestUnderwritingError, latestUnderwritingIsLoading } =
		useAdminFetchLatestSuretyAccountUnderwriting(accountId);
	const toggleEditing = () => {
		setIsEditing(!isEditing);
	};
	if (latestUnderwritingError || (!latestUnderwritingIsLoading && !latestUnderwriting)) return null;

	return (
		<AdminSectionLayout
			testId="admin-account-model-size-section"
			header={<AdminSectionHeaderLayout label="Model Size" toggleEditing={toggleEditing} />}
			isEditing={isEditing}
			modal={
				latestUnderwriting && (
					<ModelSizeModal
						accountId={accountId}
						modelSize={latestUnderwriting.modelSize}
						onClose={toggleEditing}
					/>
				)
			}
		>
			{latestUnderwritingIsLoading ? (
				<CenteredSpinner />
			) : (
				<LineItem label="Current Model Size">
					{latestUnderwriting?.modelSize ?? "Not available"}
				</LineItem>
			)}
		</AdminSectionLayout>
	);
};

const ModelSizeModal = ({
	accountId,
	modelSize,
	onClose,
}: {
	accountId: SuretyAccountId;
	modelSize: ContractSuretyAccountUnderwritingModelSize;
	onClose: () => void;
}) => {
	const { updateModelSize, updateModelSizeIsLoading, updateModelSizeSuccess } =
		useAdminUpdateModelSize(accountId, SuretyType.contract);
	const formMethods = useForm<
		AdminModelSizeFormData,
		// biome-ignore lint/suspicious/noExplicitAny: legacy any
		any,
		{ modelSize: ContractSuretyAccountUnderwritingModelSize }
	>({
		defaultValues: modelSizeToFormData(modelSize),
		reValidateMode: "onBlur",
		resolver: typedZodResolver(
			z.object({
				modelSize: contractSuretyAccountUnderwritingModelSizeSchema,
			}),
		),
	});

	const handleSubmit = ({
		modelSize,
	}: {
		modelSize: ContractSuretyAccountUnderwritingModelSize;
	}) => {
		updateModelSize(modelSize);
	};

	useEffect(() => {
		if (updateModelSizeSuccess) {
			onClose();
		}
	}, [updateModelSizeSuccess, onClose]);

	return (
		<AdminEditModalLayout
			onClose={onClose}
			onSubmit={formMethods.handleSubmit(handleSubmit)}
			form={
				<form className="w-full h-full md:w-[35vw] md:h-[245px] py-[35px] px-[45px]">
					<FormField id="modelSize">
						<FormItemLabel>Change Model Size?</FormItemLabel>
						<FormDropdown
							options={[
								{ label: "Small", value: ContractSuretyAccountUnderwritingModelSize.small },
								{ label: "Medium", value: ContractSuretyAccountUnderwritingModelSize.medium },
								{ label: "Large", value: ContractSuretyAccountUnderwritingModelSize.large },
							]}
							control={formMethods.control}
							name="modelSize"
						/>
					</FormField>
				</form>
			}
			header={"Edit Model Size"}
			isLoading={updateModelSizeIsLoading}
		/>
	);
};
